import { useCallback, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from 'react-query';
import { useDebounce } from 'shared/hooks/useDebounce';
import { getSuperAdminDiseaseList } from '../api/getSuperAdminDiseaseList';
import { masterDataKeys, masterDataStaleTime } from '../api/queryKeys';

const SEARCH_DEBOUNCE_MS = 400;
const DEFAULT_PAGE_LIMIT = 50;
const FIRST_PAGE = 1;
const INITIAL_PARAMS = { page: FIRST_PAGE, search: '' };

const getPageRows = (page) => (Array.isArray(page?.data) ? page.data : []);

/**
 * Next page number, or `undefined` when done. Uses `meta.last_page` when sent,
 * else loaded rows vs `meta.total`. An empty page always ends the list.
 */
const getNextDiseasePage = (lastPage, allPages) => {
  const pages = allPages ?? [];
  if (getPageRows(lastPage).length === 0) return undefined;
  const lastPageNumber = Number(lastPage?.meta?.last_page);
  if (lastPageNumber > 0) {
    return pages.length < lastPageNumber ? pages.length + 1 : undefined;
  }
  const total = Number(lastPage?.meta?.total);
  const loadedRows = pages.reduce(
    (count, page) => count + getPageRows(page).length,
    0
  );
  return loadedRows < total ? pages.length + 1 : undefined;
};

/**
 * Company-scoped super-admin disease (ICD-10) options with debounced search
 * and infinite scroll, shaped for `SelectInfiniteScroll`.
 *
 * `meta.total`: `parseDiseaseList` validates only `data` (BE `meta` passes
 * through as-is). First page's `meta.total` when numeric, else synthesized
 * from `hasNextPage` so the component's `hasMore` stops at the last page.
 *
 * `setParams` adapter (called with an updater fn OR a plain object). Only
 * `page` / `search` are kept — `isSearch` and the component's `limit` never
 * reach the API. New search → page 1 (fresh query key). Same search + higher
 * page → `fetchNextPage()` if a next page exists and none is in flight;
 * `params.page` only advances when that request is sent. Anything else (e.g.
 * a page-1 reset with the same search) is a no-op — loaded pages stay valid.
 *
 * @param {Object} [options]
 * @param {number|string} [options.companyId] - `company_id`; disabled if missing
 * @param {boolean} [options.enabled=true] - e.g. only while the dropdown is open
 * @param {number} [options.limit=50] - page size
 * @param {string} [options.sort] - e.g. `'classification'`
 * @returns {{ diseases: DiseaseItem[], meta: { total: number },
 *   params: { page: number, search: string }, setParams: Function,
 *   isLoading: boolean, isError: boolean, isFetchingNextPage: boolean }}
 *   `diseases` = every loaded page flattened; `params.search` = raw input.
 */
export const useSuperAdminDiseaseSearch = ({
  companyId,
  enabled = true,
  limit = DEFAULT_PAGE_LIMIT,
  sort,
} = {}) => {
  const [params, setParamsState] = useState(INITIAL_PARAMS);
  // Write-through mirror: SelectInfiniteScroll calls `setParams` from a 1s
  // timeout, so updaters must see the latest params or a new search is lost.
  const paramsRef = useRef(INITIAL_PARAMS);
  const debouncedSearch = useDebounce(params.search, SEARCH_DEBOUNCE_MS);

  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    masterDataKeys.superAdminDiseasesPaged({
      company_id: companyId,
      search: debouncedSearch,
      limit,
      sort,
    }),
    ({ pageParam = FIRST_PAGE, signal }) =>
      getSuperAdminDiseaseList(
        {
          company_id: companyId,
          search: debouncedSearch || undefined,
          page: pageParam,
          limit,
          sort,
        },
        signal
      ),
    {
      enabled: Boolean(enabled && companyId),
      staleTime: masterDataStaleTime.BATCH,
      keepPreviousData: true,
      getNextPageParam: getNextDiseasePage,
    }
  );

  const diseases = useMemo(
    () => (data?.pages ?? []).flatMap(getPageRows),
    [data]
  );

  const serverTotal = data?.pages?.[0]?.meta?.total;
  const syntheticTotal = hasNextPage ? diseases.length + 1 : diseases.length;
  const total = Number.isFinite(serverTotal) ? serverTotal : syntheticTotal;

  const setParams = useCallback(
    (updaterOrObject) => {
      const current = paramsRef.current;
      const next =
        typeof updaterOrObject === 'function'
          ? updaterOrObject(current)
          : updaterOrObject;
      const nextSearch = next?.search ?? '';

      if (nextSearch !== current.search) {
        const resetParams = { page: FIRST_PAGE, search: nextSearch };
        paramsRef.current = resetParams;
        setParamsState(resetParams);
        return;
      }

      const isNextPageRequested = (next?.page ?? FIRST_PAGE) > current.page;
      const canFetchNextPage = Boolean(hasNextPage) && !isFetchingNextPage;
      if (!isNextPageRequested || !canFetchNextPage) return;

      const advancedParams = { page: current.page + 1, search: current.search };
      paramsRef.current = advancedParams;
      setParamsState(advancedParams);
      fetchNextPage();
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  return {
    diseases,
    meta: { total },
    params,
    setParams,
    isLoading,
    isError,
    isFetchingNextPage,
  };
};
